import * as React from "react"
import { useCallback, useMemo, useEffect, useRef } from "react"
import styled from "styled-components/macro"
import { MapState, POI, useMapStore } from "@state/mapStore"
import DirectionsPanel from "./DirectionsPanel"
import IconLabel from "@common/ui/IconLabel"
import { averageDistance } from "@lib/utils/google.maps"
import TextButton from "@common/ui/TextButton"
import { useStore } from "@state/store"

export const MapSidebar: React.FC<{
  map?: google.maps.Map | null
  categories: string[]
  withDirections: boolean
  mainMarker: POI
  pois: POI[]
  panel: any
}> = ({ map, withDirections, pois, panel, categories, mainMarker }) => {
  const contentControls = useStore((s) => s.contentControls)
  const {
    routeSteps,
    activeSection,
    setActiveSection,
    activePOI,
    setActivePOI,
    setTravelMode,
    setSearchBoxPrediction,
    setSearchBoxSuggestions,
  } = useMapStore((state: MapState) => ({
    routeSteps: state.routeSteps,
    activePOI: state.activePOI,
    setActivePOI: state.setActivePOI,
    activeSection: state.activeSection,
    setActiveSection: state.setActiveSection,
    setTravelMode: state.setTravelMode,
    setSearchBoxPrediction: state.setSearchBoxPrediction,
    setSearchBoxSuggestions: state.setSearchBoxSuggestions,
  }))

  const categoryIndex = contentControls?.index
  const currentCategory = categories[categoryIndex]

  useEffect(() => {
    setTravelMode("TRANSIT")
    setActiveSection("pois")
    setActivePOI(null)
    setSearchBoxPrediction(null)
    setSearchBoxSuggestions(null)
  }, [currentCategory])

  const filteredPlaces = useMemo(() => {
    if (currentCategory === "All") {
      return pois
    } else {
      return pois.filter((place) => place.category === currentCategory)
    }
  }, [pois, categoryIndex])

  const handleBoxClick = useCallback(
    (place: POI) => {
      setActivePOI(place)
    },
    [setActivePOI],
  )

  const boxesRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (activePOI) {
      const index = filteredPlaces.findIndex(
        (place) => place.name === activePOI.name,
      )
      if (index !== -1 && boxesRef.current[index]) {
        boxesRef.current[index]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    }

    // if (currentCategory && categoriesZoomConfig[currentCategory]) {
    //   const config = categoriesZoomConfig[currentCategory]

    //   map.setOptions({
    //     center: mainMarker.coordinates,
    //     zoom: config.zoom,
    //   })
    // }
  }, [filteredPlaces, activePOI])

  useEffect(() => {
    if (activePOI) {
      setActivePOI(null)
    }
  }, [contentControls])

  const onClose = () => {
    setTravelMode("TRANSIT")
    setActiveSection("pois")
    setActivePOI(null)
    setSearchBoxPrediction(null)
    setSearchBoxSuggestions(null)
  }

  return (
    <>
      <Boxes>
        {filteredPlaces?.map((place: POI, index: number) => (
          <Box
            className={`map-poi-box ${
              place?.name === activePOI?.name ? "active" : ""
            }`}
            key={place + index.toString()}
            ref={(el) => (boxesRef.current[index] = el)}
            active={place?.name === activePOI?.name ? 1 : undefined}
            onClick={() => {
              handleBoxClick(place)
            }}
          >
            <div className="top-section">
              <div className="info">
                <div className="title">{place.name}</div>
                <div className="subtitle">{place?.description}</div>
              </div>
              <div className="poi-indicator">
                {index < 9 ? `0${index + 1}` : index + 1}
                <div className="corner top-left"></div>
                <div className="corner bottom-right"></div>
              </div>
            </div>

            <IconLabel
              label={averageDistance(place?.distances)}
              variant="ghost"
            />

            <div className="divider" />
            <div className="times">
              <IconLabel
                icon={`/assets/icons/tube.svg`}
                iconColor="#252525d6"
                label={place?.distances?.TRANSIT?.duration}
                variant="ghost"
              />
              <IconLabel
                icon={`/assets/icons/walking.svg`}
                iconColor="#252525d6"
                label={place?.distances?.WALKING?.duration}
                variant="ghost"
              />
              <IconLabel
                icon={`/assets/icons/driving.svg`}
                iconColor="#252525d6"
                label={place?.distances?.DRIVING?.duration}
                variant="ghost"
              />
              <IconLabel
                icon={`/assets/icons/cycling.svg`}
                iconColor="#252525d6"
                label={place?.distances?.BICYCLING?.duration}
                variant="ghost"
              />
            </div>

            {withDirections && (
              <TextButton
                color="var(--black)"
                style={{
                  width: "100%",
                  marginTop: "1em",
                  fontFamily: "ThirdFont-Regular",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  border: "1px solid var(--black)",
                }}
                variant="text"
                onClick={() => {
                  setActiveSection("directions")
                  setActivePOI(place)
                }}
              >
                Get Directions
              </TextButton>
            )}
          </Box>
        ))}
      </Boxes>
      <Directions
        className={
          withDirections === true && activeSection === "directions"
            ? "visible"
            : "hidden"
        }
      >
        <DirectionsPanel
          panelRef={panel}
          routeSteps={routeSteps}
          onClose={onClose}
        />
      </Directions>
    </>
  )
}

const Boxes = styled.div`
  position: relative;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2em;
  overflow-y: auto;
  height: 100%;
  width: 25%;
  touch-action: pan-y;

  &.visible {
    opacity: 1;
    z-index: 1;
    transition: all 1s;
  }

  &.hidden {
    opacity: 0;
    z-index: -1;
    transition: all 0.3s;
  }

  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  & * {
    touch-action: pan-y;
  }
`

const Box = styled.div<{ active: number | undefined }>`
  min-height: 15em;
  width: 100%;
  padding: 2rem;
  flex: 0 0 auto;
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  color: var(--black);
  font-family: ThirdFont-Regular;

  .divider {
    height: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    margin: 1rem 0;
  }

  .info {
    width: calc(100% - 3em);
  }

  .get-directions {
    width: 100%;
    background-color: transparent;
    border: 1px solid #fff;
    padding: 0.5em 1em;
    font-size: 0.7em;
    cursor: pointer;
    text-transform: uppercase;
  }

  .top-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-family: PrimaryFont;
  }

  .title {
    font-size: 1.5em;
    font-weight: bold;
    transition: color 0.3s;
  }

  .subtitle {
    font-size: 1.1em;
    opacity: 0.7;
    padding: 0.5em 0;
  }

  .distance {
  }

  .times {
    display: grid;
    grid-template-columns: 1fr 1fr;
    font-family: SecondaryFont;

    .time {
      font-family: SecondaryFont;
      display: flex;
      gap: 1em;
      padding: 1em 0;

      & span {
        font-weight: bold;
      }
    }
  }

  .poi-indicator {
    position: absolute;
    top: 0;
    right: 0;
    height: 4em;
    width: 4em;
    min-height: 3em;
    min-width: 3em;
    background-color: var(--black);
    color: var(--primary);
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s;

    .corner {
      opacity: ${(props) => (props.active ? 1 : 0)};
      position: absolute;
      width: 10px;
      height: 10px;
      transition: all 0.3s;
    }
  }
`

const Directions = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  touch-action: pan-y;
  pointer-events: none;

  & * {
    touch-action: pan-y;
  }

  &.visible {
    opacity: 1;
    z-index: 1;
    transition: all 1s;
  }

  &.hidden {
    opacity: 0;
    z-index: -1;
    transition: all 0.3s;
  }

  .close {
    position: absolute;
    width: 100%;
    bottom: 0;
    border: 1px solid #fff;
    background-color: var(--accent);
    padding: 0.5em 1em;
    font-size: 1em;
    cursor: pointer;
    text-transform: uppercase;
  }
`
